/**
 * โหมดการแสดงผล — เก็บค่าที่ผู้ใช้เลือกไว้ข้ามการเปิดปิดแอป
 *
 * ค่าถูกเขียนลง <html data-mode> ซึ่ง tokens.css ใช้เป็นตัวสลับชุดสีทั้งหมด
 * ตัวแอปเองไม่รู้จักสีเลย รู้จักแค่ชื่อโหมด
 */
import { getPref, setPref } from './db';

export const THEME_MODES = ['auto', 'day', 'sun', 'night'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const THEME_LABELS_TH: Record<ThemeMode, string> = {
  auto: 'อัตโนมัติ',
  day: 'ปกติ',
  sun: 'กลางแดด',
  night: 'กลางคืน',
};

export const THEME_HINTS_TH: Record<ThemeMode, string> = {
  auto: 'ตามการตั้งค่าของเครื่อง',
  day: 'ใช้งานทั่วไปในร่ม',
  sun: 'คอนทราสต์สูง ตัวอักษรใหญ่ สำหรับกลางแดดจัด',
  night: 'พื้นดำ ประหยัดแบตเตอรี่ สำหรับกะเช้ามืดและกลางคืน',
};

const PREF_KEY = 'theme-mode';
const DEFAULT_MODE: ThemeMode = 'auto';

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

export function applyThemeMode(mode: ThemeMode): void {
  document.documentElement.dataset.mode = mode;

  // แถบสถานะของเบราว์เซอร์บนมือถือต้องเปลี่ยนตามด้วย ไม่งั้นจะเห็นแถบสีเดิมค้างอยู่
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  if (meta) {
    const background = getComputedStyle(document.documentElement)
      .getPropertyValue('--surface')
      .trim();
    if (background) meta.content = background;
  }
}

export async function loadThemeMode(): Promise<ThemeMode> {
  try {
    const stored = await getPref<ThemeMode>(PREF_KEY);
    return isThemeMode(stored) ? stored : DEFAULT_MODE;
  } catch {
    // IndexedDB ใช้ไม่ได้ (เช่นโหมดส่วนตัวบางเบราว์เซอร์) — ไม่ควรทำให้แอปเปิดไม่ขึ้น
    return DEFAULT_MODE;
  }
}

export async function saveThemeMode(mode: ThemeMode): Promise<void> {
  applyThemeMode(mode);
  try {
    await setPref(PREF_KEY, mode);
  } catch {
    // เก็บไม่ได้ก็ใช้ไปก่อนในรอบนี้
  }
}
